import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { coverAiUpsellAlwaysShown } from 'fym-shared/src/music24/coverEditorPlans';
import { TypedMemo } from '../../common/types';
import { useCoverEditor } from '../CoverEditorContext';
import { coverEditorMessages } from '../coverEditorMessages';
import { Segmented } from '../inspector/controls/Segmented';
import { AiPublicUpsell } from '../publicMode/AiPublicUpsell';
import { AiTab } from './picture/AiTab';
import { firstPictureTab, type PictureTab } from './picture/pictureTabs';
import { BankTab } from './picture/BankTab';
import { ColourTab } from './picture/ColourTab';
import { UploadTab } from './picture/UploadTab';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the cover editor; each part uses its own slice of it */
import styles from '../CoverEditor.module.scss';
/* eslint-enable css-modules/no-unused-class */

/**
 * The Picture panel (design cover-editor b1, c): four ways to the background of the cover: AI,
 * the bank, an own upload, a colour. A plan with no AI opens on the bank. The public tool shows its
 * own AI card: no AI call starts with no account. On the Free plan the AI card is pinned above the
 * panel (`SidePanel`), so the AI tab shows only when the song has AI pictures from before.
 */
const PicturePanelComponent: React.FC = () => {
  const intl = useIntl();
  const { data, mode, publicTool } = useCoverEditor();
  const [tab, setTab] = useState<PictureTab>(() =>
    firstPictureTab({ plan: data.plan, welcomeRun: data.welcomeRun, pictureCount: data.pictures.length })
  );
  const hasAiTab = mode === 'public' || !coverAiUpsellAlwaysShown(data.plan) || data.pictures.length > 0;
  const tabs = useMemo(() => {
    const ai = { value: 'ai' as const, label: intl.formatMessage(coverEditorMessages.tabAi) };
    const free = [
      { value: 'bank' as const, label: intl.formatMessage(coverEditorMessages.tabBank) },
      { value: 'upload' as const, label: intl.formatMessage(coverEditorMessages.tabUpload) },
      { value: 'colour' as const, label: intl.formatMessage(coverEditorMessages.tabColour) },
    ];
    if (!hasAiTab) {
      return free;
    }
    // The public tool leads with what is free; the AI tab stays as the last tab (design g1, g2).
    return mode === 'public' ? [...free, ai] : [ai, ...free];
  }, [hasAiTab, intl, mode]);
  const openBank = useCallback(() => setTab('bank'), []);
  return (
    <>
      <h3 className={styles.paneTitle}>{intl.formatMessage(coverEditorMessages.toolPicture)}</h3>
      <Segmented
        label={intl.formatMessage(coverEditorMessages.toolPicture)}
        options={tabs}
        value={tab}
        onChange={setTab}
      />
      {tab === 'ai' && mode === 'public' && publicTool !== undefined && (
        <AiPublicUpsell tool={publicTool} onOpenBank={openBank} />
      )}
      {tab === 'ai' && mode !== 'public' && <AiTab onOpenBank={openBank} />}
      {tab === 'bank' && <BankTab />}
      {tab === 'upload' && <UploadTab />}
      {tab === 'colour' && <ColourTab />}
    </>
  );
};

export const PicturePanel = TypedMemo(PicturePanelComponent);
